import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import { useWatch } from 'react-hook-form';
import AppMockup, { MockupBalance, defaultSettings } from '../../appArt/components/AppMockup';
import { HomeSectionView } from '../../appArt/components/homeSections';
import {
  CarouselCard, HomeItem, HomeSectionDef, mergeWithCatalog, resolveSectionCatalog, showWindowStatus,
} from './homeItems';

/**
 * A Home do app desenhada a partir do que está no formulário.
 *
 * Reaproveita o mockup do estúdio de arte: a mesma tela de 390pt, com as seções
 * ilustradas na cor da rede. Aqui ele recebe a lista configurada em vez da home
 * de exemplo — e é isso que a lista do editor não consegue mostrar: a ordem vista
 * de cima, o título que o cliente vai LER e a arte dos blocos no formato real.
 */

const PREVIEW_WIDTH = 300;
const DEMO_POINTS = 1300;
const CAROUSEL_SECTION_ID = 'carousel';

// Içado porque é renderizado dentro de um `{cond && <JSX/>}`, e ali o plugin de
// i18n embrulha os valores do `sx` — "center" traduzido quebraria o alinhamento.
const EMPTY_SX = { py: 6, textAlign: 'center', fontSize: 12 };
const NOTE_SX = { fontSize: 11, maxWidth: PREVIEW_WIDTH, textAlign: 'center' };

const formatAmount = (value: number) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Lista do editor → o que o mockup sabe desenhar.
 *
 * Os títulos saem daqui CRUS, sem `__t`: `defaultTitle` é o literal cravado no
 * build do app, que só fala português, e `title` é texto do operador. Traduzir
 * qualquer um dos dois faria a prévia prometer uma tela que não existe.
 */
const toSectionViews = (
  items: HomeItem[],
  sectionById: Map<string, HomeSectionDef>,
  carouselImages: { id: string; src: string }[],
): HomeSectionView[] =>
  items.flatMap<HomeSectionView>(item => {
    if (item.visible === false) { return []; }

    if (item.kind === 'section') {
      const title = item.title || sectionById.get(item.id)?.defaultTitle;
      if (item.id !== CAROUSEL_SECTION_ID) { return [{ kind: 'section', id: item.id, title }]; }
      // Como no `ImageCarousel` do app: sem card dentro da janela, a seção não aparece.
      return carouselImages.length ? [{ kind: 'section', id: item.id, title, images: carouselImages }] : [];
    }

    const images = (item.blocks || [])
      .filter(block => !!block.imagePath)
      .map(block => ({ id: block.id, src: block.imagePath as string }));

    // Linha sem nenhuma arte escolhida não chega a existir no app.
    return images.length
      ? [{ kind: 'row', id: item.id, title: item.title || null, layout: item.layout || 'full', images }]
      : [];
  });

const HomePreview: React.FC = () => {
  // O record desta tela é a StationChain inteira (o GET de chains-config responde
  // `{ ...chain }`), então cor, logo e slogan já estão na mão — buscar de novo
  // traria o cadastro inteiro, credenciais de integração incluídas.
  const chain = useRecordContext();
  const homeItems = useWatch({ name: 'appHomeItems' }) as HomeItem[] | undefined;
  const pointsLabel = useWatch({ name: 'appLabel.pointsBalanceLabel' }) as string | null | undefined;
  const cashbackLabel = useWatch({ name: 'appLabel.cashbackBalanceLabel' }) as string | null | undefined;
  const showCashback = !!useWatch({ name: 'firebaseConfig.show_cashback_at_home' });
  const carouselCards = useWatch({ name: 'firebaseConfig.home_cards' }) as CarouselCard[] | undefined;

  // Os cards que estão no ar agora, ignorando etiqueta: a prévia é a Home de um cliente
  // qualquer, e esconder card segmentado faria o operador achar que a imagem sumiu.
  const carouselImages = useMemo(
    () => (carouselCards || [])
      .filter(card => !!card.imagePath && showWindowStatus(card) === 'live')
      .map((card, index) => ({ id: `${index}:${card.imagePath}`, src: card.imagePath as string })),
    [carouselCards],
  );

  const catalog = useMemo(
    () => resolveSectionCatalog(chain?.availableHomeSections as HomeSectionDef[] | undefined),
    [chain?.availableHomeSections],
  );
  const sectionById = useMemo(() => new Map(catalog.map(section => [section.id, section])), [catalog]);

  // Digitar no título de uma LINHA grava no formulário a cada tecla. Em prioridade
  // baixa, o React descarta a renderização da prévia interrompida pela tecla
  // seguinte: ela redesenha quando o operador para, em vez de a cada caractere.
  const deferredItems = useDeferredValue(homeItems);
  const sections = useMemo(
    () => toSectionViews(mergeWithCatalog(deferredItems, catalog), sectionById, carouselImages),
    [deferredItems, catalog, sectionById, carouselImages],
  );

  // Mesma regra do `HeaderBalance` do app: o cashback só entra no carrossel com o
  // fator de conversão preenchido, senão não há valor para mostrar.
  const cashbackFactor = Number(chain?.cashbackFactor) || 0;
  const hasCashback = showCashback && cashbackFactor > 0;
  const balances = useMemo<MockupBalance[]>(() => {
    const list = [{ name: pointsLabel || 'Saldo em Pontos', amount: formatAmount(DEMO_POINTS) }];
    if (hasCashback) {
      list.push({ name: cashbackLabel || 'Saldo Cashback', amount: `R$ ${formatAmount(DEMO_POINTS * cashbackFactor)}` });
    }
    return list;
  }, [pointsLabel, cashbackLabel, hasCashback, cashbackFactor]);
  // Mesmo motivo das seções: o nome do saldo também grava a cada tecla.
  const deferredBalances = useDeferredValue(balances);

  // O slide à mostra segue o campo que o operador está mexendo.
  const [activeBalance, setActiveBalance] = useState(0);
  const previous = useRef({ pointsLabel, cashbackLabel, hasCashback });
  useEffect(() => {
    const before = previous.current;
    if (cashbackLabel !== before.cashbackLabel || hasCashback !== before.hasCashback) {
      setActiveBalance(hasCashback ? 1 : 0);
    } else if (pointsLabel !== before.pointsLabel) {
      setActiveBalance(0);
    }
    previous.current = { pointsLabel, cashbackLabel, hasCashback };
  }, [pointsLabel, cashbackLabel, hasCashback]);

  if (!chain) { return null; }

  const settings = defaultSettings({
    color: chain.appColor,
    headerLabelColor: chain.appHeaderLabelColor,
    signUpButtonColor: chain.appSignUpButtonColor,
    signInButtonColor: chain.appSignInButtonColor,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <AppMockup
        screen="home"
        art={null}
        logo={chain.logoPath || null}
        name={chain.appNameInsideApp || chain.appName || chain.name}
        slogan={chain.appSlogan || ''}
        settings={settings}
        width={PREVIEW_WIDTH}
        sections={sections}
        scrollable
        balances={deferredBalances}
        activeBalance={Math.min(activeBalance, deferredBalances.length - 1)}
        onPickBalance={setActiveBalance}
      />
      {sections.length === 0 && (
        <Typography color="text.secondary" sx={EMPTY_SX}>Nada visível na Home.</Typography>
      )}
      {showCashback && !hasCashback && (
        <Typography color="warning.main" sx={NOTE_SX}>
          O saldo em cashback só aparece com o valor do cashback para 1 ponto preenchido em Configurações › Geral.
        </Typography>
      )}
      <Typography color="text.secondary" sx={NOTE_SX}>
        Ilustrações de exemplo; o conteúdo real vem do cadastro da rede.
      </Typography>
    </Box>
  );
};

export default HomePreview;
